import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { VisibleColumnType } from "@air/api/types";
import { Check as CheckIcon, EyeClosed, Field as FieldIcon, TriangleDown } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { useBreakpointsContext } from "@air/provider-media-query";
import { memo, useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { KanbanColumnHeaderDisplay } from "~/components/KanbanColumnHeaderDisplay";
import { DropdownMenuWithActionSheet } from "~/components/Menus/DropdownMenuWithActionSheet";
import { KANBAN_GROUP_BY_MENU } from "~/constants/testIDs";
import { setKanbanGroupByAction, setKanbanVisibleColumnsAction, showKanbanColumnAction } from "~/store/configViews/actions";
import { currentKanbanGroupBySelector, currentViewTypeNameSelector, kanbanGroupByOptionsSelector, visibleColumnIdsSelector } from "~/store/configViews/selectors";
import { useAirSelector } from "~/utils/ReduxUtils";
export const ViewKanbanGroupByMenu = /*#__PURE__*/ memo((param)=>{
    let { customFieldValues } = param;
    const [buttonFlashStyles, setButtonFlashStyles] = useState({});
    const groupByField = useAirSelector(currentKanbanGroupBySelector, shallowEqual);
    const [fetchedCustomField, setFetchedCustomField] = useState((groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId) || null);
    const dispatch = useDispatch();
    const currentViewType = useAirSelector(currentViewTypeNameSelector);
    const groupByOptions = useAirSelector(kanbanGroupByOptionsSelector);
    const { isAboveMediumScreen } = useBreakpointsContext();
    const visibleColumnIds = useAirSelector(visibleColumnIdsSelector);
    const [prevVisibleColumnIds, setPrevVisibleColumnIds] = useState(visibleColumnIds);
    useEffect(()=>{
        if (!!currentViewType && !groupByField && groupByOptions.length) {
            // first option will exist, as will its customFieldId
            const firstCFID = groupByOptions[0].customFieldId;
            dispatch(setKanbanGroupByAction({
                customFieldId: firstCFID
            }));
        }
    }, [
        groupByField,
        groupByOptions,
        dispatch,
        currentViewType
    ]);
    useEffect(()=>{
        // when groupByField changes, new columns fetched
        // set them in redux in order to be saved and read from
        if (!fetchedCustomField && (groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId)) {
            return setFetchedCustomField(groupByField.customFieldId);
        }
        if ((groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId) !== fetchedCustomField && (customFieldValues === null || customFieldValues === void 0 ? void 0 : customFieldValues.length)) {
            setFetchedCustomField((groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId) || null);
            dispatch(setKanbanVisibleColumnsAction({
                customFieldValues
            }));
        }
    }, [
        dispatch,
        groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId,
        fetchedCustomField,
        customFieldValues
    ]);
    useEffect(()=>{
        if (!visibleColumnIds) return;
        if (!prevVisibleColumnIds) {
            return setPrevVisibleColumnIds(visibleColumnIds);
        }
        if (visibleColumnIds.length < prevVisibleColumnIds.length) {
            setButtonFlashStyles({
                backgroundColor: "#FFE784"
            });
            setTimeout(()=>{
                setButtonFlashStyles({});
            }, 1000);
        }
        setPrevVisibleColumnIds(visibleColumnIds);
    }, [
        prevVisibleColumnIds,
        visibleColumnIds
    ]);
    const hiddenColumnsOptions = useMemo(()=>{
        if (!visibleColumnIds || !customFieldValues) return undefined;
        const columns = [];
        if (!visibleColumnIds.includes(VisibleColumnType.unassignedCustomFieldValue)) {
            const onClick = ()=>dispatch(showKanbanColumnAction({
                    column: {
                        type: VisibleColumnType.unassignedCustomFieldValue
                    }
                }));
            columns.push({
                id: VisibleColumnType.unassignedCustomFieldValue,
                label: /*#__PURE__*/ _jsx(KanbanColumnHeaderDisplay, {
                    id: VisibleColumnType.unassignedCustomFieldValue,
                    color: "transparent",
                    label: "None"
                }),
                suffix: /*#__PURE__*/ _jsx(EyeClosed, {
                    className: "size-4 text-pigeon-400"
                }),
                onSelect: onClick,
                type: "item"
            });
        }
        customFieldValues.forEach((cfValue)=>{
            if (!visibleColumnIds.includes(cfValue.id)) {
                const onClick = ()=>dispatch(showKanbanColumnAction({
                        column: {
                            type: VisibleColumnType.customFieldValue,
                            customFieldValueId: cfValue.id,
                            value: cfValue.value
                        }
                    }));
                columns.push({
                    id: cfValue.id,
                    label: /*#__PURE__*/ _jsx(KanbanColumnHeaderDisplay, {
                        id: VisibleColumnType.unassignedCustomFieldValue,
                        color: cfValue.color.name,
                        label: cfValue.value
                    }),
                    suffix: /*#__PURE__*/ _jsx(EyeClosed, {
                        className: "size-4 text-pigeon-400"
                    }),
                    onSelect: onClick,
                    type: "item"
                });
            }
        });
        return columns;
    }, [
        dispatch,
        visibleColumnIds,
        customFieldValues
    ]);
    const options = useMemo(()=>{
        if (!groupByOptions) return [];
        const opts = [];
        if ((hiddenColumnsOptions === null || hiddenColumnsOptions === void 0 ? void 0 : hiddenColumnsOptions.length) && isAboveMediumScreen) {
            opts.push({
                id: "hiddenColumns",
                label: "Hidden Columns",
                options: hiddenColumnsOptions,
                type: "sub"
            });
        }
        groupByOptions.forEach((opt)=>{
            const onClick = ()=>dispatch(setKanbanGroupByAction({
                    customFieldId: opt.customFieldId
                }));
            var _opt_customFieldId;
            opts.push({
                id: (_opt_customFieldId = opt === null || opt === void 0 ? void 0 : opt.customFieldId) !== null && _opt_customFieldId !== void 0 ? _opt_customFieldId : "",
                label: opt.name,
                prefix: /*#__PURE__*/ _jsx(FieldIcon, {
                    className: "flex size-4 text-pigeon-400"
                }),
                suffix: (groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId) === opt.customFieldId ? /*#__PURE__*/ _jsx(CheckIcon, {
                    className: "size-4"
                }) : null,
                onSelect: onClick,
                type: "item"
            });
        });
        if ((hiddenColumnsOptions === null || hiddenColumnsOptions === void 0 ? void 0 : hiddenColumnsOptions.length) && !isAboveMediumScreen) {
            opts.push({
                id: "separator",
                type: "separator"
            });
            opts.push({
                id: "hiddenColumns",
                label: "Hidden Columns",
                type: "title"
            });
            hiddenColumnsOptions.forEach((col)=>{
                opts.push(col);
            });
        }
        return opts;
    }, [
        isAboveMediumScreen,
        hiddenColumnsOptions,
        dispatch,
        groupByField === null || groupByField === void 0 ? void 0 : groupByField.customFieldId,
        groupByOptions
    ]);
    if (!groupByOptions.length || !groupByField) return null;
    return /*#__PURE__*/ _jsx(DropdownMenuWithActionSheet, {
        title: "Group by: ".concat(groupByField.name),
        trigger: /*#__PURE__*/ _jsxs(Button, {
            appearance: "ghost",
            color: "grey",
            "data-testid": KANBAN_GROUP_BY_MENU,
            size: "medium",
            suffix: /*#__PURE__*/ _jsx(TriangleDown, {
                className: "size-4"
            }),
            style: buttonFlashStyles,
            children: [
                "Group by: ",
                groupByField.name
            ]
        }),
        options: options
    });
});
ViewKanbanGroupByMenu.displayName = "ViewKanbanGroupByMenu";
